import {
  AssignmentTurnedIn,
  BookmarkBorder,
  Cloud,
  CloudOutlined,
  Lock,
  QuestionMark,
  Schedule as Clock,
} from "@mui/icons-material"
import SpeedIcon from "@mui/icons-material/Speed"
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing"
import { Tooltip } from "@material-ui/core"
import { cloudBadgeStyle, themePalette } from "./styles"
import { normalizeAssignmentNodeName } from "./devicesSettings.utils"

// Renderiza la insignia visual segun el estado del acceso a la nube.
// Vive fuera de la pantalla porque se usa en ambos paneles y no depende de estado local.
export const renderCloudAccessBadge = (meter, options = {}) => {
  const { compact = false, listLabel = false, listIconOnly = false } = options
  if (!meter) return null

  if (meter.cloudAccess === "permanente") {
    return (
      <Tooltip title="Acceso a la nube permanente">
        <span
          style={{
            ...cloudBadgeStyle,
            padding: listIconOnly ? "0.28rem 0.34rem" : compact ? "0.2rem 0.35rem" : cloudBadgeStyle.padding,
            minWidth: compact ? "unset" : undefined,
            backgroundColor: themePalette.surface.accentSoft,
            color: themePalette.primary.main,
            border: `1px solid ${themePalette.surface.accentBorder}`,
          }}
        >
          <span
            style={{
              position: "relative",
              width: "1rem",
              height: "1rem",
              marginRight: listIconOnly || compact ? 0 : "0.15rem",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Cloud
              fontSize="small"
              style={{
                position: "absolute",
                color: themePalette.surface.soft,
              }}
            />
            <CloudOutlined
              fontSize="small"
              style={{
                position: "absolute",
                color: themePalette.primary.main,
              }}
            />
          </span>
          {!compact && !listIconOnly && (listLabel ? "Acceso Permanente" : "Acceso Nube Permanente")}
        </span>
      </Tooltip>
    )
  }

  if (meter.cloudAccess === "trial") {
    const remainingDaysLabel = meter.remainingDays === 1 ? "1 dia restante" : `${meter.remainingDays} dias restantes`
    const trialTooltip = meter.trialExpired
      ? "Acceso de prueba a la nube expirado"
      : `Acceso de prueba a la nube${typeof meter.remainingDays === "number" ? ` (${remainingDaysLabel})` : ""}`
    const trialLabel = meter.trialExpired
      ? "Acceso de prueba expirado"
      : typeof meter.remainingDays === "number"
        ? `Acceso de prueba a la nube (${remainingDaysLabel})`
        : "Acceso de prueba a la nube"

    return (
      <Tooltip title={trialTooltip}>
        <span
          style={{
            ...cloudBadgeStyle,
            padding: listIconOnly ? "0.28rem 0.34rem" : compact ? "0.2rem 0.35rem" : cloudBadgeStyle.padding,
            minWidth: compact ? "unset" : undefined,
            backgroundColor: "rgba(245, 158, 11, 0.14)",
            color: "#b45309",
            border: "1px solid rgba(245, 158, 11, 0.22)",
          }}
        >
          <Clock fontSize="small" />
          {!compact && !listIconOnly && (listLabel ? (meter.trialExpired ? "Prueba expirada" : "Prueba") : trialLabel)}
        </span>
      </Tooltip>
    )
  }

  return (
    <span
      style={{
        ...cloudBadgeStyle,
        padding: listIconOnly ? "0.28rem 0.34rem" : compact ? "0.2rem 0.35rem" : cloudBadgeStyle.padding,
        minWidth: compact ? "unset" : undefined,
        backgroundColor: themePalette.surface.page,
        color: themePalette.text.disabled,
        border: `1px solid ${themePalette.border.muted}`,
      }}
    >
      <Lock fontSize="small" />
      {!compact && !listIconOnly && "Sin acceso a la nube"}
    </span>
  )
}

// Renderiza el indicador visual de asignacion a nodo.
export const renderAssignmentNodeIcon = (meter) => {
  const normalizedAssignmentNodeName = normalizeAssignmentNodeName(meter?.assigmentNodeName)
  const hasAssignmentNode = Boolean(normalizedAssignmentNodeName)
  const tooltipTitle = hasAssignmentNode
    ? normalizedAssignmentNodeName
    : "Dispositivo no asignado"

  return (
    <Tooltip title={tooltipTitle}>
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {hasAssignmentNode ? (
          <AssignmentTurnedIn fontSize="small" style={{ color: themePalette.text.disabled }} />
        ) : (
          <BookmarkBorder fontSize="small" style={{ color: themePalette.text.disabled }} />
        )}
      </span>
    </Tooltip>
  )
}

const getStatusTooltip = (active, online) => {
  if (!active) return "su dispositivo esta inactivo"
  return online ? "su dispositivo esta online" : "su dispositivo esta offline"
}

const getStatusIconColor = (active, online) => {
  if (!active) return themePalette.status.inactive
  return online ? themePalette.status.online : themePalette.status.offline
}

const isAutomateProduct = (productName = "") => String(productName || "").toLowerCase().includes("automate")

// Renderiza el icono de estado segun el tipo de equipo.
export const renderStatusIcon = (productName, active, online) => {
  const normalizedProductName = String(productName ?? "").trim()
  if (!normalizedProductName) {
    return (
      <Tooltip title="Modelo de dispositivo desconocido">
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <QuestionMark style={{ color: themePalette.text.disabled }} fontSize="small" />
        </span>
      </Tooltip>
    )
  }

  const iconColor = getStatusIconColor(active, online)
  const tooltipTitle = getStatusTooltip(active, online)
  const automateIconColor = themePalette.text.disabled
  const isAutomate = isAutomateProduct(normalizedProductName)

  if (isAutomate) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <PrecisionManufacturingIcon style={{ color: automateIconColor }} fontSize="small" />
      </span>
    )
  }

  return (
    <Tooltip title={tooltipTitle}>
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <SpeedIcon style={{ color: iconColor }} fontSize="small" />
      </span>
    </Tooltip>
  )
}
